import { PhotoDraft, PhotoSection, SECTIONS, productKeyOf } from "./photoModel";
import { ProductInfo } from "./useProductValidation";
import { ItemState } from "./useUploadQueue";

/**
 * Builds the four-level view of a dropped batch — barcode / matcode / section / file —
 * so the screen mirrors the folder tree the uploader actually built:
 *
 *   8851989064178/          barcode
 *     22C-0798/             matcode
 *       cover/              section
 *         <file>.png        file
 *
 * The same four levels are what `parsePhotoDraft` requires on the way in and what the
 * S3 key preserves on the way out, so nothing here invents structure.
 */

export type TreeKind = "barcode" | "matcode" | "section" | "file";

/** Counts rolled up from the files beneath a node. */
export interface RollupStats {
  photos: number;
  unknown: number;
  dup: number;
  invalid: number;
  checking: number;
  uploading: number;
  success: number;
  failed: number;
}

export interface FileNode {
  kind: "file";
  id: string;
  draft: PhotoDraft;
  conflict: boolean;
  upload?: ItemState;
}

export interface SectionNode {
  kind: "section";
  id: string;
  barcode: string;
  matCode: string;
  section: PhotoSection;
  files: FileNode[];
  fileIds: string[];
  stats: RollupStats;
}

export interface MatcodeNode {
  kind: "matcode";
  id: string;
  barcode: string;
  matCode: string;
  info?: ProductInfo;
  sections: SectionNode[];
  fileIds: string[];
  stats: RollupStats;
}

export interface BarcodeNode {
  kind: "barcode";
  id: string;
  barcode: string;
  matcodes: MatcodeNode[];
  fileIds: string[];
  stats: RollupStats;
}

export type TreeNode = BarcodeNode | MatcodeNode | SectionNode | FileNode;

export interface BuildTreeInput {
  drafts: PhotoDraft[];
  productInfo: Record<string, ProductInfo>;
  fileNames: Record<string, string>;
  conflicts: Set<string>;
  uploads: Record<string, ItemState>;
  /** Section ids that existed earlier in this batch and must keep a row even at 0 files. */
  ghostSections?: Set<string>;
}

const emptyStats = (): RollupStats => ({
  photos: 0,
  unknown: 0,
  dup: 0,
  invalid: 0,
  checking: 0,
  uploading: 0,
  success: 0,
  failed: 0,
});

const addStats = (into: RollupStats, from: RollupStats): RollupStats => {
  into.photos += from.photos;
  into.unknown += from.unknown;
  into.dup += from.dup;
  into.invalid += from.invalid;
  into.checking += from.checking;
  into.uploading += from.uploading;
  into.success += from.success;
  into.failed += from.failed;
  return into;
};

/** Photo-level counts for one set of files. Product validity is a matcode concern. */
function statsOfFiles(files: FileNode[]): RollupStats {
  const stats = emptyStats();
  for (const f of files) {
    stats.photos += 1;
    if (f.draft.direction === null) stats.unknown += 1;
    if (f.conflict) stats.dup += 1;
    if (f.upload?.status === "uploading") stats.uploading += 1;
    if (f.upload?.status === "success") stats.success += 1;
    if (f.upload?.status === "failed") stats.failed += 1;
  }
  return stats;
}

export const sectionIdOf = (barcode: string, matCode: string, section: PhotoSection) =>
  `${barcode}/${matCode}/${section}`;

/** The S3 key a draft will occupy once committed — the same shape `deriveNames` produces. */
export const uploadKeyOf = (draft: PhotoDraft, fileName?: string) =>
  fileName ? `original/${draft.barcode}/${draft.matCode}/${draft.section}/${fileName}` : "";

/** True when a node holds something the uploader still has to resolve. */
export const needsAttention = (stats: RollupStats): boolean =>
  stats.unknown > 0 || stats.dup > 0 || stats.invalid > 0 || stats.failed > 0;

export function buildTree({
  drafts,
  productInfo,
  fileNames,
  conflicts,
  uploads,
  ghostSections,
}: BuildTreeInput): BarcodeNode[] {
  // barcode -> matcode -> section -> files, keyed so an empty section can be reinstated below.
  const byBarcode = new Map<string, Map<string, Map<PhotoSection, FileNode[]>>>();

  const touch = (barcode: string, matCode: string, section: PhotoSection) => {
    const mats = byBarcode.get(barcode) ?? new Map();
    byBarcode.set(barcode, mats);
    const sections = mats.get(matCode) ?? new Map();
    mats.set(matCode, sections);
    const files = sections.get(section) ?? [];
    sections.set(section, files);
    return files;
  };

  for (const draft of drafts) {
    const files = touch(draft.barcode, draft.matCode, draft.section);
    files.push({
      kind: "file",
      id: draft.id,
      draft,
      conflict: conflicts.has(uploadKeyOf(draft, fileNames[draft.id])),
      upload: uploads[draft.id],
    });
  }

  // A section emptied by a move keeps its row: it existed in what the user dropped.
  for (const ghost of Array.from(ghostSections ?? [])) {
    const parts = ghost.split("/");
    if (parts.length !== 3) continue;
    const [barcode, matCode, rawSection] = parts;
    const section = rawSection as PhotoSection;
    if (!SECTIONS.includes(section)) continue;
    // Only reinstate under a matcode that still has files somewhere.
    if (!byBarcode.get(barcode)?.has(matCode)) continue;
    touch(barcode, matCode, section);
  }

  const barcodes: BarcodeNode[] = [];

  for (const barcode of Array.from(byBarcode.keys()).sort()) {
    const matsMap = byBarcode.get(barcode)!;
    const matcodes: MatcodeNode[] = [];

    for (const matCode of Array.from(matsMap.keys()).sort()) {
      const sectionsMap = matsMap.get(matCode)!;
      const productKey = productKeyOf(barcode, matCode);
      const info = productInfo[productKey];
      const sections: SectionNode[] = [];

      const present = SECTIONS.filter((s) => sectionsMap.has(s));
      for (const section of present) {
        const files = [...(sectionsMap.get(section) ?? [])].sort(
          (a, b) => a.draft.order - b.draft.order || a.id.localeCompare(b.id)
        );
        const stats = statsOfFiles(files);
        sections.push({
          kind: "section",
          id: sectionIdOf(barcode, matCode, section),
          barcode,
          matCode,
          section,
          files,
          fileIds: files.map((f) => f.id),
          stats,
        });
      }

      const stats = sections.reduce((acc, s) => addStats(acc, s.stats), emptyStats());
      // Product validity is a property of the matcode, not of any one photo.
      if (info?.status === "invalid") stats.invalid = 1;
      if (info?.status === "checking" || info === undefined) stats.checking = 1;

      matcodes.push({
        kind: "matcode",
        id: productKey,
        barcode,
        matCode,
        info,
        sections,
        fileIds: sections.flatMap((s) => s.fileIds),
        stats,
      });
    }

    barcodes.push({
      kind: "barcode",
      id: barcode,
      barcode,
      matcodes,
      fileIds: matcodes.flatMap((m) => m.fileIds),
      stats: matcodes.reduce((acc, m) => addStats(acc, m.stats), emptyStats()),
    });
  }

  return barcodes;
}

/**
 * Opens what the uploader has to act on, and then as much of the rest as fits.
 *
 * Every branch holding an unresolved photo is expanded down to the offending file, so
 * the work is never hidden. The remaining clean branches are opened breadth-first while
 * the visible row count stays within `budget` — a small drop lands fully open, a large
 * one lands scannable, and neither needs a magic per-level rule.
 */
export function defaultExpanded(tree: BarcodeNode[], budget = 40): Set<string> {
  const expanded = new Set<string>();

  const openAttention = (node: BarcodeNode | MatcodeNode | SectionNode): boolean => {
    let opened = false;
    if (node.kind === "section") {
      opened = needsAttention(node.stats);
    } else {
      const children: (MatcodeNode | SectionNode)[] =
        node.kind === "barcode" ? node.matcodes : node.sections;
      for (const child of children) if (openAttention(child)) opened = true;
      if (needsAttention(node.stats)) opened = true;
    }
    if (opened) expanded.add(node.id);
    return opened;
  };
  tree.forEach(openAttention);

  const visibleRows = (): number => {
    let rows = 0;
    const walkSection = (s: SectionNode) => {
      rows += 1;
      if (expanded.has(s.id)) rows += s.files.length;
    };
    const walkMat = (m: MatcodeNode) => {
      rows += 1;
      if (expanded.has(m.id)) m.sections.forEach(walkSection);
    };
    for (const b of tree) {
      rows += 1;
      if (expanded.has(b.id)) b.matcodes.forEach(walkMat);
    }
    return rows;
  };

  // Breadth-first: every barcode, then every matcode, then every section.
  const levels: TreeNode[][] = [
    tree,
    tree.flatMap((b) => b.matcodes),
    tree.flatMap((b) => b.matcodes.flatMap((m) => m.sections)),
  ];
  for (const level of levels) {
    for (const node of level) {
      if (expanded.has(node.id)) continue;
      expanded.add(node.id);
      if (visibleRows() > budget) {
        expanded.delete(node.id);
        return expanded;
      }
    }
  }
  return expanded;
}

export type BoardFilter = "all" | "todo" | "dup" | "pending";

/** A photo still owed to storage: it failed, or it was never eligible to be sent. */
export const isPending = (f: FileNode): boolean => f.upload?.status !== "success";

/**
 * Drops individual photos and rebuilds the counts above them, so a folder that keeps one
 * unfinished photo out of four reports "1 รูป" rather than the four it was dropped with.
 */
export function prunePhotos(tree: BarcodeNode[], keep: (f: FileNode) => boolean): BarcodeNode[] {
  const out: BarcodeNode[] = [];
  for (const barcode of tree) {
    const matcodes: MatcodeNode[] = [];
    for (const matcode of barcode.matcodes) {
      const sections: SectionNode[] = [];
      for (const section of matcode.sections) {
        const files = section.files.filter(keep);
        if (files.length === 0) continue;
        sections.push({
          ...section,
          files,
          fileIds: files.map((f) => f.id),
          stats: statsOfFiles(files),
        });
      }
      if (sections.length === 0) continue;
      const stats = sections.reduce((acc, s) => addStats(acc, s.stats), emptyStats());
      if (matcode.info?.status === "invalid") stats.invalid = 1;
      if (matcode.info?.status === "checking" || matcode.info === undefined) stats.checking = 1;
      matcodes.push({
        ...matcode,
        sections,
        fileIds: sections.flatMap((s) => s.fileIds),
        stats,
      });
    }
    if (matcodes.length === 0) continue;
    out.push({
      ...barcode,
      matcodes,
      fileIds: matcodes.flatMap((m) => m.fileIds),
      stats: matcodes.reduce((acc, m) => addStats(acc, m.stats), emptyStats()),
    });
  }
  return out;
}

/**
 * Narrows the tree to the matcodes worth looking at. Pruning happens at the matcode level
 * because that is where a product's identity lives — a barcode that loses all its matcodes
 * drops out rather than lingering as an empty heading.
 */
export function filterTree(tree: BarcodeNode[], filter: BoardFilter, query: string): BarcodeNode[] {
  const q = query.trim().toLowerCase();
  // "pending" narrows to individual photos; the others narrow to whole matcodes.
  const scoped = filter === "pending" ? prunePhotos(tree, isPending) : tree;
  const keep = (m: MatcodeNode) => {
    if (q && !m.barcode.toLowerCase().includes(q) && !m.matCode.toLowerCase().includes(q)) return false;
    if (filter === "todo") return m.stats.unknown > 0 || m.stats.invalid > 0;
    if (filter === "dup") return m.stats.dup > 0;
    return true;
  };
  if ((filter === "all" || filter === "pending") && q === "") return scoped;
  return scoped
    .map((b) => {
      const matcodes = b.matcodes.filter(keep);
      return {
        ...b,
        matcodes,
        fileIds: matcodes.flatMap((m) => m.fileIds),
        // Roll-ups must describe what is on screen, not what was filtered away.
        stats: matcodes.reduce((acc, m) => addStats(acc, m.stats), emptyStats()),
      };
    })
    .filter((b) => b.matcodes.length > 0);
}

export type CheckState = "checked" | "indeterminate" | "unchecked";

export function checkStateOf(fileIds: string[], selected: Set<string>): CheckState {
  if (fileIds.length === 0) return "unchecked";
  let hit = 0;
  for (const id of fileIds) if (selected.has(id)) hit += 1;
  if (hit === 0) return "unchecked";
  return hit === fileIds.length ? "checked" : "indeterminate";
}

/** All section ids currently holding at least one file — the set worth remembering as ghosts. */
export function occupiedSectionIds(drafts: PhotoDraft[]): Set<string> {
  return new Set(drafts.map((d) => sectionIdOf(d.barcode, d.matCode, d.section)));
}

/**
 * Splits a filename into the part every sibling shares and the part that tells them apart,
 * so three near-identical names stay scannable down a column.
 */
export function splitFileName(fileName: string, barcode: string, matCode: string): {
  prefix: string;
  tail: string;
} {
  const prefix = `${barcode}_${matCode}`;
  if (fileName.startsWith(prefix)) {
    return { prefix, tail: fileName.slice(prefix.length) };
  }
  return { prefix: "", tail: fileName };
}
